import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomDialog from "../modals/CustomDialog/CustomDialog";
import Select from "../forms/Select/Select";
import { defaultSelectStyles } from "../forms/Select/styles";
import { Button } from "../forms/Buttons/Button";
import StreamChoice, { STREAM_CHOICES, takenStreams } from "./StreamChoice";
import { addSubjectFolder } from "../../services/questionService";
import { getApiErrorMessage } from "../../utils/apiError";

// The select is 44px tall on phones.
const selectStyles = {
  ...defaultSelectStyles,
  control: (provided, state) => ({ ...defaultSelectStyles.control(provided, state), "@media (max-width: 639px)": { minHeight: "2.75rem" } }),
};

// Adds a subject to the exam `exam` ({uuid, name, subjects}) — admins only — for one stream (КАЗ, РУС) or both.
// `subjects` is the subject catalog; a subject the exam already has in every stream is left out, and its streams
// already there are offered disabled. A 400 {subject_uuid: [...]} (the same subject and stream added meanwhile)
// or {stream: [...]} is shown under its field.
export default function AddSubjectDialog({ isOpen, exam, subjects, onClose, onAdded }) {
  const [t] = useTranslation();
  const [subject, setSubject] = useState(null);
  const [stream, setStream] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({ subject: "", stream: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSubject(null);
      setStream(null);
      setFieldErrors({ subject: "", stream: "" });
      setError("");
    }
  }, [isOpen]);

  const options = useMemo(
    () =>
      (subjects || [])
        .filter((s) => takenStreams(exam?.subjects, s.uuid).size < STREAM_CHOICES.length)
        .map((s) => ({ value: s.uuid, label: s.name })),
    [exam, subjects]
  );
  const taken = useMemo(() => takenStreams(exam?.subjects, subject), [exam, subject]);

  // A subject starts on the first stream it does not have in the exam yet: both, else КАЗ, else РУС.
  const chooseSubject = (value) => {
    setSubject(value);
    setFieldErrors({ subject: "", stream: "" });
    const used = takenStreams(exam?.subjects, value);
    setStream(STREAM_CHOICES.find((s) => !used.has(s)) ?? null);
  };

  const handleSubmit = async () => {
    if (!subject || saving || taken.has(stream)) return;
    setSaving(true);
    setFieldErrors({ subject: "", stream: "" });
    setError("");
    try {
      onAdded(await addSubjectFolder(exam.uuid, subject, stream));
    } catch (err) {
      const data = err?.response?.data;
      if (data && !data.code && (data.subject_uuid || data.stream || data.parent)) {
        setFieldErrors({
          subject: data.subject_uuid ? t("folders.stream.duplicate") : data.parent ? [].concat(data.parent).join(" ") : "",
          stream: data.stream ? [].concat(data.stream).join(" ") : "",
        });
      } else {
        setError(getApiErrorMessage(t, err, "folders.toast.save_failed"));
      }
    } finally {
      setSaving(false);
    }
  };

  const close = () => !saving && onClose();

  return (
    <CustomDialog
      isOpen={isOpen}
      closeModal={close}
      headerText={t("folders.subject_dialog.title", { name: exam?.name || "" })}
      maxWidth="max-w-md"
    >
      {/* Esc closes the dialog from the select too, while its list is closed (react-select keeps that Esc). */}
      <div
        className="mt-4 space-y-4 text-left"
        onKeyDownCapture={(e) => {
          if (e.key === "Escape" && e.target.getAttribute?.("aria-expanded") === "false" && !e.target.value) close();
        }}
      >
        {options.length > 0 ? (
          <>
            <Select
              label={t("folders.subject_dialog.subject_label")}
              placeholder={t("folders.subject_dialog.subject_placeholder")}
              options={options}
              value={subject}
              onChange={(o) => chooseSubject(o?.value ?? null)}
              isSearchable
              errorMsg={fieldErrors.subject}
              customStyles={selectStyles}
            />
            {subject && (
              <StreamChoice
                name="add-subject-stream"
                value={stream}
                onChange={(next) => {
                  setStream(next);
                  setFieldErrors((errors) => ({ ...errors, stream: "" }));
                }}
                taken={taken}
                error={fieldErrors.stream}
                errorId="add-subject-stream-error"
                disabled={saving}
              />
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500">{t("folders.subject_dialog.none_left")}</p>
        )}
        {error && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button type="button" color="secondary-outline" text={t("buttons.cancel")} onClick={onClose} disabled={saving} className="max-sm:h-11" />
        <Button
          type="button"
          color="primary"
          text={t("buttons.add")}
          onClick={handleSubmit}
          loading={saving}
          disabled={!subject || saving || taken.has(stream)}
          className="max-sm:h-11"
        />
      </div>
    </CustomDialog>
  );
}
